'use client';

import { ArrowUpRight } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import { LinkPreview } from '@/components/ui/link-preview';
import { useTranslation } from '@/lib/i18n/context';

const SequencingCapabilities = () => {
  const { t } = useTranslation();

  const features = [
    {
      id: 1,
      title: t.features_pages['Bulk Contact Import'],
      description:
        t.features_pages[
          'Upload hundreds of thousands of contacts at once. CSV import, API integration, or sync from your CRM.'
        ],
    },
    {
      id: 2,
      title: t.features_pages['Multi-Channel Sequences'],
      description:
        t.features_pages[
          'Mix WhatsApp, AI phone calls, SMS, and email in one sequence. Start with a call, follow up via email, remind via WhatsApp.'
        ],
    },
    {
      id: 3,
      title: t.features_pages['AI-Powered Personalization'],
      description:
        t.features_pages[
          'Agent 5 personalizes every message based on customer data. Not templates—actual personalized communication.'
        ],
    },
    {
      id: 4,
      title: t.features_pages['Response-Based Branching'],
      description:
        t.features_pages[
          'Sequences adapt based on responses. Positive reply? Move to sales. Negative? Remove from sequence. AI understands intent.'
        ],
    },
    {
      id: 5,
      title: t.features_pages['API & UI Access'],
      description:
        t.features_pages[
          'Build sequences programmatically via API or use the visual editor. Full programmatic control or no-code interface—your choice.'
        ],
    },
  ];

  return (
    <section className="bg-[rgb(11,21,25)] py-32 text-white">
      <div className="container mx-auto mb-32">
        <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center">
          <h2 className="font-serif text-2xl text-white md:text-4xl">
            {t.features_pages['Reach thousands with AI personalization']}
          </h2>
          <p className="text-sm text-white/80 md:text-base">
            {
              t.features_pages[
                'Not mass messaging. Personalized outreach at scale. Each contact gets AI-crafted communication tailored to them.'
              ]
            }
          </p>
        </div>
        <div className="mb-32 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20 md:p-7"
            >
              <h3 className="font-serif text-lg text-white md:text-xl">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function OutboundSequencingPage() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.features_pages['Outbound Sequencing']}
            <br />
            {t.features_pages['with AI at scale']}
          </>
        }
        subtitle={
          t.features_pages[
            'Reach customers before they contact you — delivery delays, renewals, failed payments, appointment reminders. Voice, WhatsApp, SMS and email in one sequence, and every reply lands with the same agent that handles your support inbox.'
          ]
        }
      />

      <Logos />

      <BenefitsSection
        title={t.features_pages['Outbound Sequencing Capabilities']}
        subtitle={t.features_pages['Personalized outreach at scale']}
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="black"
        benefits={[
          {
            title: t.features_pages['AI WhatsApp Sequences'],
            description:
              t.features_pages[
                'Send personalized WhatsApp messages to your contact list. Agent 5 crafts each message based on customer data.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/outbound-sequencing/ai-whatsapp-sequences.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['AI Phone Call Campaigns'],
            description:
              t.features_pages[
                'Agent 5 calls your contact list. Natural conversations, qualification, appointment setting—all automated.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl:
              '/product/outbound-sequencing/ai-phone-call-campaigns.mp4',
            imagePosition: 'left',
          },
          {
            title: t.features_pages['SMS with AI Responses'],
            description:
              t.features_pages[
                'Send SMS campaigns with AI that responds to replies. Two-way conversations, not just broadcasts.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/outbound-sequencing/sms-with-ai-responses.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['AI Email Sequences'],
            description:
              t.features_pages[
                'Multi-step email campaigns with AI personalization. Follow-ups, drip campaigns, and targeted outreach.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/outbound-sequencing/email-sequences.mp4',
            imagePosition: 'left',
          },
        ]}
      />

      {/* Authentic Message Section */}
      <section className="overflow-hidden py-32">
        <div className="container w-full leading-relaxed">
          <h2 className="text-muted-foreground/40 mt-10 text-center font-serif text-3xl md:text-4xl">
            {isAr ? (
              <>
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Email_marketing"
                  className="px-1"
                >
                  التسويق الجماعي
                </LinkPreview>{' '}
                اتجاه واحد. أداة الإرسال الجماعي تقف عند التسليم، والعميل الذي
                يرد «طيب، تقدرون تأجلونها للخميس؟» يتحوّل إلى تذكرة عند شخص آخر.
                <br />
                <br />
                التسلسلات الصادرة تشتغل على نفس الوكيل الذي يدير صندوق الدعم
                عندك. يرسل التذكير، ويقرأ الرد، ويعيد جدولة التوصيل، ويكتب
                النتيجة في{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Customer_relationship_management"
                  className="px-1"
                >
                  سجل العميل
                </LinkPreview>{' '}
                — لأن الصادر والدعم عقل واحد، لا أداتين تتناقلان الطلب.
                <br />
                <br />
                الرسالة هي النصف السهل. الرد هو المنتج.
              </>
            ) : (
              <>
                Mass{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Email_marketing"
                  className="px-1"
                >
                  marketing
                </LinkPreview>{' '}
                is one-way. A blast tool stops at delivery, and the customer who
                replies &ldquo;actually, can you move it to Thursday?&rdquo;
                becomes somebody else&apos;s ticket.
                <br />
                <br />
                Outbound sequencing runs on the same agent as your support
                inbox. It sends the reminder, reads the reply, reschedules the
                delivery, and writes the outcome back to the{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Customer_relationship_management"
                  className="px-1"
                >
                  customer record
                </LinkPreview>{' '}
                — because outbound and support are one brain, not two tools
                handing off.
                <br />
                <br />
                The message is the easy half. The reply is the product.
              </>
            )}
          </h2>
        </div>
      </section>

      {/* Capabilities Section - Dark */}
      <SequencingCapabilities />

      {/* API Section */}
      <section className="bg-white py-32">
        <div className="container">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="mb-4 font-serif text-2xl md:text-4xl">
              {t.features_pages['API & UI Access']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.features_pages[
                  'Build sequences programmatically via API or use the visual editor. Complete control however you prefer.'
                ]
              }
            </p>
          </div>
          <div className="mx-auto max-w-4xl space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Create Sequence']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.features_pages[
                      'Create multi-step sequences with delays and filters'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /sequences
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Start Sequence']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.features_pages[
                      'Activate sequence to begin reaching contacts'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  PUT /sequences/:id/start
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Get Sequence']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.features_pages[
                      'Retrieve sequence details and performance'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  GET /sequences/:id
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Update Sequence']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t.features_pages['Modify steps, delays, and targeting']}
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  PATCH /sequences/:id
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Cancel Sequence']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t.features_pages['Stop a running sequence immediately']}
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  PUT /sequences/:id/cancel
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Add Contacts']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t.features_pages['Add contacts to continuous sequences']}
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /sequences/continuous/contacts
                </code>
              </div>
            </div>
            <div className="flex justify-center pt-8">
              <Button size="lg" asChild>
                <a
                  href="https://docs.open.cx/api-reference/sequencing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.features_pages['View Sequencing API Docs']}{' '}
                  <ArrowUpRight className="ms-2 size-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.features_pages[
            'Find answers to common questions about Outbound Sequencing.'
          ]
        }
        items={[
          {
            question:
              t.features_pages['How many contacts can I add to a sequence?'],
            answer:
              t.features_pages[
                'Hundreds of thousands. Upload contacts in bulk via CSV, API, or sync from your CRM.'
              ],
          },
          {
            question: t.features_pages['What channels can I use in sequences?'],
            answer:
              t.features_pages[
                'WhatsApp, AI phone calls, SMS, and email. Mix and match channels in one sequence.'
              ],
          },
          {
            question: t.features_pages['Is each message personalized?'],
            answer:
              t.features_pages[
                'Yes. Agent 5 personalizes every message based on customer data, conversation history, and context. Not templates—actual personalized communication.'
              ],
          },
          {
            question:
              t.features_pages['Can sequences adapt based on responses?'],
            answer:
              t.features_pages[
                'Yes. Sequences branch based on what customers say. Positive reply? Move to sales sequence. Negative? Remove from list. AI understands intent.'
              ],
          },
          {
            question: t.features_pages['Can I build sequences via API?'],
            answer:
              t.features_pages[
                'Yes. Full API access to create, start, update, and cancel sequences programmatically. Or use the visual editor.'
              ],
          },
          {
            question: t.features_pages['What use cases work best?'],
            answer:
              t.features_pages[
                'Proactive support — delivery and delay notices, appointment reminders, failed payments, renewals, and win-back. Marketing campaigns and lead nurturing run on the same sequences.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
